import React from 'react'
import {useQuery} from '@tanstack/react-query'
import {useCommerceApi, useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useSeoUrlMapping from './use-seo-url-mapping'
import {
    BR_SEARCH_URL,
    BR_SORT_OPTIONS,
    USEQUERY_OPTIONS,
    SELECTED_ATTRIBUTES_PLP_PRODUCTS,
    DEFAULT_SEARCH_PARAMS
} from '../constants'
import {useLocation} from 'react-router-dom'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {formatDate} from '../utils/product-utils'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {buildApiUrl, getOrCreateBrUid2} from '../utils/common-utils'

const useSeoCategory = (parameters, options = {enabled: true}, customerGroupIds) => {
    const queryParams = USEQUERY_OPTIONS
    const selectedAttributes = SELECTED_ATTRIBUTES_PLP_PRODUCTS
    const api = useCommerceApi()
    const appOrigin = useAppOrigin()
    const {getTokenWhenReady} = useAccessToken()
    const {getSeoUrlMapping} = useSeoUrlMapping()
    const location = useLocation()

    /** ---- Config Setup ---- **/
    const {
        app: {commerceAPI}
    } = getConfig()

    const {bloomreach, storefront} = commerceAPI || {}
    const {config} = bloomreach || {}
    const {caching, hostUrl} = storefront || {}

    let brSortingOptions = BR_SORT_OPTIONS
    let enableBloomreachSearch = null
    let searchBaseUrl = ''
    let accountId = ''

    let catalogViews = ''
    let searchCacheTime = 900 * 1000 // 15 minutes
    let productCacheTime = 15 * 1000 // 15 seconds

    try {
        const configAsJson = JSON.parse(config || '{}')
        brSortingOptions = configAsJson.sortOptions || brSortingOptions
        enableBloomreachSearch = configAsJson.enableBloomreachSearch
        searchBaseUrl = configAsJson.searchBaseUrl || 'core.dxpapi.com'
        accountId = configAsJson.accountId
        catalogViews = configAsJson.catalogViews
    } catch (e) {
        console.log(e.message)
    }
    try {
        const cachingConfig = JSON.parse(caching || '{}')
        searchCacheTime = cachingConfig.search || searchCacheTime
        productCacheTime = cachingConfig.productCacheTime || productCacheTime
    } catch (e) {
        console.log(e.message)
    }

    /** ---- Cookie ---- **/
    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: false
        })

    /**
     * ---- 1. SEO URL Mapping with Category details from SCAPI ----
     */
    const urlMappingQuery = useQuery({
        queryKey: ['use-seo-url-mapping', parameters?.urlMapping],
        queryFn: async () => {
            if (!parameters?.urlMapping?.urlSegment) {
                return null
            }
            const token = await getTokenWhenReady()
            const results = await getSeoUrlMapping(api, token, parameters.urlMapping).then(
                async (mapping) => {
                    return mapping
                }
            )
            return results
        },
        enabled: options?.enabled,
        ...queryParams
    })

    /**
     * ---- 2. Bloomreach Search ----
     */
    const categoryId = urlMappingQuery.data?.c_id
    const enabledBrQuery =
        (!!enableBloomreachSearch &&
            !!parameters?.bloomReachParams &&
            !!urlMappingQuery.isSuccess &&
            !!urlMappingQuery.data?.c_id &&
            !urlMappingQuery.isFetching) ||
        !!parameters?.search?.q
    const brSearchQuery = useQuery({
        queryKey: [
            'use-br-search',
            parameters?.restOfParams,
            parameters?.bloomReachParams,
            categoryId
        ],
        queryFn: async ({signal}) => {
            const mapping = urlMappingQuery.data
            const categoryID = mapping?.id || mapping?.c_id
            const brgDynCategory = mapping?.c_brg_dyn_category
            const categoryFilters = mapping?.c_brg_category_filters
            const searchParams = {
                ...parameters.search,
                refine: [...(parameters.search?.refine || [])]
            }

            // remove cgid
            if (searchParams?.refine?.length) {
                searchParams.refine = searchParams.refine.filter((ref) => !ref.startsWith('cgid='))
            }

            const {query, searchType, requestType, start, rows, effectiveDateTime} =
                parameters?.bloomReachParams || {}

            const formattedDate = formatDate(effectiveDateTime)
            const currentDate = formatDate()
            const disableRules = formattedDate > currentDate

            if (formattedDate && searchParams?.refine && !onClient()) {
                searchParams.refine.push(`online_date=[* TO ${formattedDate}]`)
            }

            const fqParams = searchParams?.refine?.map((entry) => {
                const [key, values] = entry.split('=')
                const orValues = values
                    ?.split('|')
                    ?.map((v) => (key === 'online_date' ? `${v}` : `"${v}"`))
                    ?.join(' OR ')
                return `${key}:${orValues}`
            })

            const sortValue =
                brSortingOptions?.find((o) => o.id === searchParams?.sort)?.value || ''
            const baseUrl = new URL(
                onClient()
                    ? `${appOrigin}${BR_SEARCH_URL}`
                    : `${'https://' + searchBaseUrl + '/api/v1/core/'}` // SSR should call bloomreach direcly - no need to go back to proxy
            )

            const fqArrayQuery = onClient() ? {fqParams: JSON.stringify(fqParams || {})} : {}
            const fqQuery = onClient() ? {} : {fq: fqParams || []}
            const merchandisingRules = disableRules
                ? {'merchandising_rules.disable': 'attribute_include, attribute_exclude'}
                : {}
            const brUid = getOrCreateBrUid2(cookie)
            const queryParams = {
                start: start,
                rows: rows,
                account_id: accountId,
                domain_key: catalogViews,
                q: query || brgDynCategory || categoryID,
                sort: sortValue,
                request_type: requestType,
                ref_url: hostUrl + location?.pathname + location?.search,
                url: hostUrl + location?.pathname + location?.search,
                content_injection: true,
                fl: 'pid,skuid,product_type',
                search_type: searchType,
                efq: categoryFilters,
                _br_uid_2: brUid,
                ...merchandisingRules,
                ...fqArrayQuery,
                ...fqQuery
            }
            const url = buildApiUrl(baseUrl, queryParams)
            const apiResponse = await fetch(url, {
                credentials: 'omit',
                signal,
                headers: {
                    formatteddate: formattedDate
                }
            })
            const jsonResposne = await apiResponse.json()
            return jsonResposne
        },
        enabled: enabledBrQuery,
        ...queryParams,
        staleTime: searchCacheTime
    })

    /**
     * ---- 3. Products from SCAPI (Split Loading: First 12 + Remaining) ----
     */
    const enabledGetProducts =
        !!enableBloomreachSearch &&
        !!brSearchQuery.isSuccess &&
        !!brSearchQuery.data?.response?.docs

    const allDocs = brSearchQuery.data?.response?.docs || []
    const contentByIndex = {}
    allDocs.forEach((doc, index) => {
        if (doc?.docType === 'content') {
            contentByIndex[index - 1] = doc.content
        }
    })
    const firstBatch = allDocs.slice(0, 12)
    const remainingBatch = allDocs.slice(12)
    const productsQuery = useQuery({
        queryKey: [
            'use-plp-products-initial',
            firstBatch,
            parameters?.search?.c_selectedCountry,
            parameters?.search?.c_selectedCurrency,
            customerGroupIds || '-'
        ],
        queryFn: async () => {
            const token = await getTokenWhenReady()
            const ids = firstBatch
                ?.map((d) => (d?.product_type === 'set' ? d.pid : d?.variants?.[0]?.skuid || d.pid))
                ?.filter(Boolean)
                ?.join(',')
            return api.shopperProducts.getProducts({
                parameters: {
                    ids,
                    allImages: true,
                    perPricebook: true,
                    select: selectedAttributes,
                    expand: [
                        'availability',
                        'links',
                        'promotions',
                        'options',
                        'images',
                        'prices',
                        'variations'
                    ],
                    c_selectedCountry: parameters?.search?.c_selectedCountry,
                    c_selectedCurrency: parameters?.search?.c_selectedCurrency,
                    c_isBloomreachSearch: true
                },
                headers: {Authorization: `Bearer ${token}`}
            })
        },
        enabled: enabledGetProducts,
        ...queryParams,
        staleTime: productCacheTime
    })

    /**
     * ---- Fetch remaining products (non-blocking) ----
     */
    const enabledRemainingProducts =
        enabledGetProducts &&
        remainingBatch.length > 0 &&
        !!productsQuery?.data?.total &&
        !productsQuery?.isFetching
    const remainingProductsQuery = useQuery({
        queryKey: [
            'use-plp-products-remaining',
            remainingBatch,
            parameters?.search?.c_selectedCountry,
            parameters?.search?.c_selectedCurrency,
            customerGroupIds || '-'
        ],
        queryFn: async () => {
            if (remainingBatch.length === 0) return []
            const token = await getTokenWhenReady()
            const allIds = remainingBatch
                ?.map((d) => (d?.product_type === 'set' ? d.pid : d?.variants?.[0]?.skuid || d.pid))
                ?.filter(Boolean)

            // getProducts has a max of 24 IDs per call — chunk accordingly
            const MAX_IDS = DEFAULT_SEARCH_PARAMS?.limit
            const chunks = []
            for (let i = 0; i < allIds.length; i += MAX_IDS) {
                chunks.push(allIds.slice(i, i + MAX_IDS))
            }

            const results = await Promise.all(
                chunks.map((chunkIds) =>
                    api.shopperProducts.getProducts({
                        parameters: {
                            ids: chunkIds.join(','),
                            allImages: true,
                            perPricebook: true,
                            select: selectedAttributes,
                            expand: [
                                'availability',
                                'links',
                                'promotions',
                                'options',
                                'images',
                                'prices',
                                'variations'
                            ],
                            c_selectedCountry: parameters?.search?.c_selectedCountry,
                            c_selectedCurrency: parameters?.search?.c_selectedCurrency,
                            c_isBloomreachSearch: true
                        },
                        headers: {Authorization: `Bearer ${token}`}
                    })
                )
            )

            // Merge chunked responses into a single response shape
            return results.reduce(
                (merged, res) => ({
                    data: [...(merged.data || []), ...(res?.data || [])],
                    limit: (merged.limit || 0) + (res?.limit || 0),
                    total: (merged.total || 0) + (res?.total || 0)
                }),
                {}
            )
        },
        enabled: enabledRemainingProducts,
        ...queryParams,
        staleTime: productCacheTime
    })

    /**
     * ---- Merge results for rendering ----
     */
    const mergedProducts = React.useMemo(() => {
        const firstResponse = productsQuery?.data || {}
        const remainingResponse = remainingProductsQuery?.data || {}

        const firstData = firstResponse?.data || []
        const restData = remainingBatch?.length ? remainingResponse?.data || [] : []

        // Combine product arrays — but first batch is available immediately
        const mergedData = [...firstData, ...restData]

        // Combine metadata separately, but show first batch metadata first
        const limit =
            firstResponse?.limit !== undefined
                ? firstResponse.limit + (remainingBatch?.length ? remainingResponse?.limit || 0 : 0)
                : undefined
        const total =
            firstResponse?.total !== undefined
                ? firstResponse.total + (remainingBatch?.length ? remainingResponse?.total || 0 : 0)
                : undefined

        return {
            data: mergedData,
            limit,
            total
        }
    }, [productsQuery?.data, remainingProductsQuery?.data])
    /**
     * ---- 4. SCAPI Product Search ----
     */
    const enabledProductsSearch =
        !!parameters?.search && !!urlMappingQuery.isSuccess && !enableBloomreachSearch
    const productSearchQuery = useQuery({
        queryKey: [
            'use-plp-search',
            parameters?.search,
            urlMappingQuery?.data?.c_id,
            customerGroupIds || '-'
        ],
        queryFn: async () => {
            const token = await getTokenWhenReady()
            const mapping = urlMappingQuery.data
            const categoryID = mapping?.id || mapping?.c_id
            const searchParams = {
                ...parameters.search,
                refine: [...(parameters.search?.refine || [])]
            }

            if (categoryID) {
                searchParams.refine.push(`cgid=${categoryID}`)
            }

            return api.shopperSearch.productSearch({
                parameters: searchParams,
                headers: {Authorization: `Bearer ${token}`}
            })
        },
        enabled: enabledProductsSearch,
        ...queryParams,
        staleTime: productCacheTime
    })

    const isBloomreachRefetching =
        brSearchQuery?.isFetching ||
        brSearchQuery?.isPreviousData ||
        productsQuery?.isFetching ||
        productsQuery?.isPreviousData

    /** ---- Final Return ---- */
    return {
        remainingProducts: remainingBatch?.length,
        remainingFetchedProducts: productSearchQuery?.isFetching
            ? 0
            : remainingProductsQuery?.data?.data?.length,
        remainingProductsError: remainingProductsQuery?.isError,
        data:
            urlMappingQuery?.data || parameters?.search?.q
                ? {
                      brResponse: brSearchQuery?.data?.response,
                      brRefinements: brSearchQuery?.data?.facet_counts,
                      productSearchResult: enableBloomreachSearch
                          ? mergedProducts
                          : productSearchQuery?.data,
                      scapiSearch: productSearchQuery?.data,
                      category: urlMappingQuery.data,
                      brKeywordRedirect: brSearchQuery?.data?.keywordRedirect,
                      brAutoCorrectQuery: brSearchQuery?.data?.autoCorrectQuery,
                      brDidYouMean: brSearchQuery?.data?.did_you_mean,
                      contentByIndex: contentByIndex,
                      campaign: brSearchQuery?.data?.campaign
                  }
                : undefined,
        isLoading: urlMappingQuery?.isError
            ? false
            : enableBloomreachSearch
            ? brSearchQuery?.isLoading || productsQuery?.isLoading
            : productSearchQuery?.isLoading,
        isFetched: urlMappingQuery?.isError
            ? false
            : enableBloomreachSearch
            ? brSearchQuery?.isFetched && productsQuery?.isFetched
            : productSearchQuery?.isFetched,
        isError: urlMappingQuery?.isError
            ? true
            : enableBloomreachSearch
            ? brSearchQuery?.isError || productsQuery?.isError
            : productSearchQuery?.isError,
        isRefetching: urlMappingQuery?.isError
            ? false
            : enableBloomreachSearch
            ? isBloomreachRefetching
            : productSearchQuery?.isRefetching
    }
}

export default useSeoCategory
